import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Send, Heart, Flag, Pin, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { getSessionId } from "@/lib/session";
import {
  sendCommunityMessage,
  likeCommunityMessage,
  reportCommunityMessage,
} from "@/lib/community.functions";
import { trackEvent } from "@/lib/audit";
import { useLanguage } from "@/hooks/useLanguage";
import { filterProfanity, looksAbusive } from "@/lib/community/profanityFilter";
import { setInputFocus } from "@/lib/mobileFloatingControls";
import { setLayerActive } from "@/lib/pr30/zeroOverlapPolicy";

interface CommunityMessage {
  id: string;
  session_id: string;
  author_name: string;
  content: string;
  is_pinned: boolean;
  is_hidden: boolean;
  like_count: number;
  report_count: number;
  created_at: string;
}

const EMOJIS = ["⚽", "🔥", "👏", "😱", "🇪🇸", "🇦🇷"];

/** Stable demo seed shown only when there are zero real messages.
 *  Pure presentation — never written to the database, never sent over realtime. */
const DEMO_SEED: CommunityMessage[] = [
  { id: "seed-1", session_id: "seed-1", author_name: "Fan-MX42", content: "Qué presión la del medio campo 🔥", is_pinned: false, is_hidden: false, like_count: 12, report_count: 0, created_at: new Date(Date.now() - 1000 * 60 * 6).toISOString() },
  { id: "seed-2", session_id: "seed-2", author_name: "Fan-AR07", content: "Si meten el lateral por dentro ganan el partido", is_pinned: false, is_hidden: false, like_count: 8, report_count: 0, created_at: new Date(Date.now() - 1000 * 60 * 4).toISOString() },
  { id: "seed-3", session_id: "seed-3", author_name: "Fan-BR19", content: "Ese 10 está jugando otro deporte 😱", is_pinned: false, is_hidden: false, like_count: 21, report_count: 0, created_at: new Date(Date.now() - 1000 * 60 * 3).toISOString() },
  { id: "seed-4", session_id: "seed-4", author_name: "Fan-ES88", content: "VAR otra vez… ⚽ revisen la jugada", is_pinned: false, is_hidden: false, like_count: 5, report_count: 0, created_at: new Date(Date.now() - 1000 * 60 * 2).toISOString() },
  { id: "seed-5", session_id: "seed-5", author_name: "Fan-CO33", content: "Ritmo brutal, esto se define en los últimos 15 👏", is_pinned: false, is_hidden: false, like_count: 17, report_count: 0, created_at: new Date(Date.now() - 1000 * 60 * 1).toISOString() },
  { id: "seed-6", session_id: "seed-6", author_name: "Fan-PT54", content: "Si entra Vitinha cambia todo el bloque", is_pinned: false, is_hidden: false, like_count: 3, report_count: 0, created_at: new Date(Date.now() - 1000 * 30).toISOString() },
];

/** Deterministic pseudo-random integer in [min,max] derived from a string id.
 *  Same matchId → same number, so SSR and client agree (no hydration mismatch). */
function stableOnline(seed: string, min = 142, max = 318): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const n = Math.abs(h) % (max - min + 1);
  return min + n;
}

export function CommunityChat({ matchId = "live" }: { matchId?: string }) {
  const { t } = useLanguage();
  // Unique per-instance suffix — prevents Supabase channel topic collisions
  // when multiple CommunityChat instances share the same matchId.
  const instanceId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [messages, setMessages] = useState<CommunityMessage[]>([]);
  const [input, setInput] = useState("");
  const [online, setOnline] = useState(() => stableOnline(matchId));
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionId = useMemo(() => getSessionId(), []);
  const likedRef = useRef<Set<string>>(new Set());
  const sendFn = useServerFn(sendCommunityMessage);
  const likeFn = useServerFn(likeCommunityMessage);
  const reportFn = useServerFn(reportCommunityMessage);

  // P0-04 — client-side retention: hide messages older than 24h, cap to 500.
  const RETENTION_MS = 24 * 60 * 60 * 1000;
  const HARD_CAP = 500;
  const pruneOld = (list: CommunityMessage[]): CommunityMessage[] => {
    const cutoff = Date.now() - RETENTION_MS;
    const fresh = list.filter((m) => {
      const ts = new Date(m.created_at).getTime();
      return Number.isFinite(ts) ? ts >= cutoff : true;
    });
    return fresh.length > HARD_CAP ? fresh.slice(-HARD_CAP) : fresh;
  };

  useEffect(() => {
    void trackEvent("community_chat_opened", { match_id: matchId });
    void supabase
      .from("community_chat_messages")
      .select("*")
      .eq("match_id", matchId)
      .eq("is_hidden", false)
      .order("created_at", { ascending: true })
      .limit(120)
      .then(({ data }) => {
        if (data) setMessages(pruneOld(data as unknown as CommunityMessage[]));
      });

    const channel = supabase
      .channel(`community-${matchId}-${instanceId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "community_chat_messages", filter: `match_id=eq.${matchId}` },
        (payload) => {
          const m = payload.new as unknown as CommunityMessage;
          if (m.is_hidden) return;
          setMessages((prev) => {
            // Dedupe: drop optimistic placeholder from same session with identical content
            const withoutOptimistic = prev.filter(
              (p) =>
                !(
                  p.id.startsWith("optimistic-") &&
                  p.session_id === m.session_id &&
                  p.content === m.content
                ),
            );
            if (withoutOptimistic.some((p) => p.id === m.id)) return withoutOptimistic;
            return pruneOld([...withoutOptimistic, m]);
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "community_chat_messages", filter: `match_id=eq.${matchId}` },
        (payload) => {
          const m = payload.new as unknown as CommunityMessage;
          setMessages((prev) =>
            m.is_hidden ? prev.filter((p) => p.id !== m.id) : prev.map((p) => (p.id === m.id ? m : p)),
          );
        },
      )
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const real = Object.keys(state).length;
        setOnline(real > 1 ? real : stableOnline(matchId));
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({ session_id: sessionId, at: Date.now() });
        }
      });
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [matchId, sessionId, instanceId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = input.trim();
    if (!raw) return;
    const clean = filterProfanity(raw).slice(0, 280);
    setInput("");
    const author = `Fan-${sessionId.slice(0, 4)}`;
    // Optimistic insert — message appears instantly while server fn round-trips.
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: CommunityMessage = {
      id: optimisticId,
      session_id: sessionId,
      author_name: author,
      content: clean,
      is_pinned: false,
      is_hidden: false,
      like_count: 0,
      report_count: 0,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev.slice(-119), optimistic]);
    const result = await sendFn({
      data: { match_id: matchId, session_id: sessionId, author_name: author, content: clean },
    });
    if (result.ok) {
      void trackEvent("community_message_sent", { len: clean.length, flagged: looksAbusive(raw) });
    } else {
      // Roll back the optimistic message on failure
      setMessages((prev) => prev.filter((p) => p.id !== optimisticId));
    }
  };

  const like = async (m: CommunityMessage) => {
    if (likedRef.current.has(m.id)) return;
    likedRef.current.add(m.id);
    await likeFn({ data: { message_id: m.id } });
    void trackEvent("community_message_liked", { id: m.id });
  };

  const report = async (m: CommunityMessage) => {
    await reportFn({ data: { message_id: m.id } });
    void trackEvent("community_message_reported", { id: m.id });
  };

  const pinned = messages.filter((m) => m.is_pinned);
  const trending = [...messages].sort((a, b) => b.like_count - a.like_count).slice(0, 3).filter((m) => m.like_count > 0);
  const displayMessages = messages.length === 0 ? DEMO_SEED : messages;
  const isDemo = messages.length === 0;

  // "En vivo" indicator — true when any real message was posted in the last 60s.
  const isLive = !isDemo && messages.some((m) => {
    const ts = new Date(m.created_at).getTime();
    return Number.isFinite(ts) && Date.now() - ts < 60_000;
  });

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl glass">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
        <div className="text-xs font-bold">{t("community_chat_title")}</div>
        {isLive && (
          <span className="flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-red-500">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
            {t("community_live_now") || "En vivo"}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
          <Users className="h-3 w-3" /> {online}
        </div>
      </div>

      {pinned.length > 0 && (
        <div className="border-b border-border/60 bg-accent/20 px-3 py-2">
          {pinned.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-[11px]">
              <Pin className="mt-0.5 h-3 w-3 text-primary" />
              <span className="font-semibold">{m.author_name}:</span>
              <span>{m.content}</span>
            </div>
          ))}
        </div>
      )}

      {trending.length > 0 && (
        <div className="border-b border-border/60 bg-secondary/30 px-3 py-1.5 text-[10px] text-muted-foreground">
          🔥 {trending.map((m) => `${m.author_name} (${m.like_count})`).join(" · ")}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {isDemo && (
          <div className="mb-1 flex items-center justify-center">
            <span className="rounded-full bg-accent/50 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-muted-foreground">
              Demo · conversación de muestra
            </span>
          </div>
        )}
        {displayMessages.map((m) => {
          const mine = !isDemo && m.session_id === sessionId;
          return (
            <div key={m.id} className={`flex flex-col gap-0.5 ${mine ? "items-end" : "items-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-[13px] ${mine ? "bg-primary text-primary-foreground" : "bg-secondary/70"} ${isDemo ? "opacity-90" : ""}`}>
                <div className="text-[10px] opacity-70">{m.author_name}</div>
                <div>{m.content}</div>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <button
                  onClick={() => !isDemo && like(m)}
                  disabled={isDemo}
                  className="flex items-center gap-0.5 hover:text-primary disabled:cursor-default disabled:hover:text-muted-foreground"
                  aria-label={t("community_like")}
                >
                  <Heart className="h-3 w-3" /> {m.like_count}
                </button>
                <button
                  onClick={() => !isDemo && report(m)}
                  disabled={isDemo}
                  className="hover:text-destructive disabled:cursor-default disabled:hover:text-muted-foreground"
                  aria-label={t("community_report")}
                >
                  <Flag className="h-3 w-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border/60 px-2 py-1.5 flex flex-wrap gap-1">
        {EMOJIS.map((e) => (
          <button key={e} onClick={() => setInput((v) => v + e)} className="rounded-full bg-accent/40 px-2 py-0.5 text-sm hover:bg-accent">
            {e}
          </button>
        ))}
      </div>

      <form
        onSubmit={send}
        className="flex items-center gap-2 border-t border-border/60 px-3 py-2"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("community_chat_placeholder")}
          maxLength={280}
          aria-label={t("community_chat_placeholder")}
          onFocus={() => {
            setInputFocus(true);
            setLayerActive("chat_input", true);
            void trackEvent("community_input_safe_area_activated", { match_id: matchId });
            void trackEvent("chat_input_obstruction_prevented", { match_id: matchId });
          }}
          onBlur={() => {
            setInputFocus(false);
            setLayerActive("chat_input", false);
            void trackEvent("community_input_safe_area_restored", { match_id: matchId });
          }}
          className="flex-1 min-w-0 rounded-xl bg-accent/40 px-3 py-2.5 text-base sm:text-sm outline-none placeholder:text-muted-foreground/70 focus:bg-accent"
        />
        <button
          type="submit"
          aria-label={t("community_send")}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground disabled:opacity-50"
          disabled={!input.trim()}
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}